import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, MessageSquare, Send, Search, UserPlus, UserMinus, Loader2 } from 'lucide-react';
import { chime } from '../lib/chime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Messages() {
  const { user } = useAuth();
  const isPremium = ['pro', 'studio'].includes((user?.tier || '').toLowerCase());
  const [privateMode, setPrivateMode] = useState(false);
  const [params, setParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [activeUser, setActiveUser] = useState(null); // {user_id, name, email}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [followingSet, setFollowingSet] = useState(new Set());
  const bottomRef = useRef(null);

  const loadThreads = async () => {
    try {
      const r = await axios.get(`${API}/dm/threads`, { withCredentials: true });
      setThreads(r.data?.threads || []);
    } catch { /* ignore */ }
  };
  const loadFollows = async () => {
    try {
      const r = await axios.get(`${API}/follow/me`, { withCredentials: true });
      setFollowingSet(new Set((r.data?.following || []).map(f => f.target_id)));
    } catch { /* ignore */ }
  };

  useEffect(() => { loadThreads(); loadFollows(); axios.post(`${API}/dm/mark_read`, {}, { withCredentials: true }).catch(() => {}); }, []);

  useEffect(() => {
    const uid = params.get('to');
    if (!uid || uid === activeUser?.user_id) return;
    openThread(uid);
  }, [params, activeUser?.user_id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const openThread = async (uid) => {
    try {
      const r = await axios.get(`${API}/dm/${uid}`, { withCredentials: true });
      setActiveUser(r.data?.other || { user_id: uid, name: 'user' });
      setMessages(r.data?.messages || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load thread');
    }
  };

  const send = async () => {
    if (!activeUser?.user_id || !text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/dm/${activeUser.user_id}`, { text: text.trim(), private: !!(isPremium && privateMode) }, { withCredentials: true });
      setMessages(prev => [...prev, r.data.message]);
      setText('');
      chime('info');
      loadThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setBusy(false); }
  };

  const doSearch = async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    try {
      const r = await axios.get(`${API}/users/search?q=${encodeURIComponent(q.trim())}`, { withCredentials: true });
      setResults((r.data?.users || []).filter(u => u.user_id !== user?.user_id));
    } catch { setResults([]); }
  };

  const toggleFollow = async (uid) => {
    try {
      if (followingSet.has(uid)) {
        await axios.delete(`${API}/follow/${uid}`, { withCredentials: true });
        setFollowingSet(s => { const c = new Set(s); c.delete(uid); return c; });
      } else {
        await axios.post(`${API}/follow/${uid}`, {}, { withCredentials: true });
        setFollowingSet(s => new Set(s).add(uid));
        chime('success');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Follow toggle failed'); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="messages-page">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-sm text-zinc-400">
            <MessageSquare className="w-4 h-4 text-[#06B6D4]" />
            <span className="font-mono text-xs">{threads.length} THREADS</span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex max-w-[1400px] w-full mx-auto overflow-hidden flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-72 border-b md:border-b-0 md:border-r border-[#1E1E24] bg-[#0A0A0B] flex flex-col max-h-[40vh] md:max-h-none">
          <div className="p-3 border-b border-[#1E1E24]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => doSearch(e.target.value)}
                placeholder="Search creators…"
                className="pl-7 h-8 bg-transparent border-[#1E1E24] text-xs"
                data-testid="messages-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {results.length > 0 && (
              <div className="border-b border-[#1E1E24] py-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 px-3 py-1">Search results</div>
                {results.map(u => (
                  <div key={u.user_id} className="px-3 py-1.5 hover:bg-white/5 flex items-center gap-2">
                    <button
                      onClick={() => { setParams({ to: u.user_id }); setSearch(''); setResults([]); }}
                      className="flex-1 text-left text-sm text-white truncate"
                      data-testid={`messages-user-${u.user_id}`}
                    >
                      {u.name || u.email?.split('@')[0]}
                      {u.role === 'admin' && <span className="ml-1 text-[9px] font-mono text-amber-400">ADMIN</span>}
                    </button>
                    <button
                      onClick={() => toggleFollow(u.user_id)}
                      className="text-zinc-400 hover:text-[#06B6D4]"
                      title={followingSet.has(u.user_id) ? 'Unfollow' : 'Follow'}
                    >
                      {followingSet.has(u.user_id) ? <UserMinus className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {threads.length === 0 ? (
              <p className="text-xs text-zinc-500 text-center py-6">No conversations yet. Search a creator to start.</p>
            ) : (
              threads.map(t => (
                <button
                  key={t.pair}
                  onClick={() => setParams({ to: t.other })}
                  className={`w-full text-left px-3 py-2 border-b border-[#1E1E24]/60 hover:bg-white/5 ${activeUser?.user_id === t.other ? 'bg-[#06B6D4]/10' : ''}`}
                  data-testid={`messages-thread-${t.other}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white truncate">{t.other_name}</span>
                    <span className="text-[9px] font-mono text-zinc-600">{(t.last_at || '').slice(11, 16)}</span>
                  </div>
                  <p className="text-[11px] text-zinc-500 truncate">
                    {t.last_from === user?.user_id ? 'You: ' : ''}{t.last_message}
                  </p>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Thread */}
        <main className="flex-1 flex flex-col overflow-hidden bg-[#050505]">
          {!activeUser ? (
            <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
              Pick a conversation or search a creator to start.
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#1E1E24] flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#06B6D4]/20 flex items-center justify-center text-[#06B6D4] font-bold">
                  {(activeUser.name || activeUser.email || '?')[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="text-sm text-white">{activeUser.name || activeUser.email?.split('@')[0]}</div>
                  <div className="text-[10px] font-mono text-zinc-500">{activeUser.email}</div>
                </div>
                <div className="flex-1" />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleFollow(activeUser.user_id)}
                  className="h-7 text-xs border-[#1E1E24] hover:border-[#06B6D4]"
                  data-testid="messages-follow-toggle"
                >
                  {followingSet.has(activeUser.user_id) ? <><UserMinus className="w-3 h-3 mr-1" /> Unfollow</> : <><UserPlus className="w-3 h-3 mr-1" /> Follow</>}
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.length === 0 && <p className="text-xs text-zinc-500 text-center py-8">Say hi 👋</p>}
                {messages.map(m => (
                  <div key={m.message_id} className={`flex ${m.from_id === user?.user_id ? 'justify-end' : ''}`}>
                    <div className={`max-w-[75%] p-2 rounded-lg text-sm whitespace-pre-wrap
                      ${m.from_id === user?.user_id ? 'bg-[#06B6D4] text-black' : 'bg-[#0A0A0C] border border-[#1E1E24] text-zinc-200'}`}
                      data-testid={`dm-msg-${m.message_id}`}
                    >
                      {m.text}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              <div className="border-t border-[#1E1E24] bg-[#0A0A0B] p-3">
                {isPremium && (
                  <label className="flex items-center gap-2 text-[11px] font-mono uppercase text-zinc-400 mb-2 cursor-pointer" data-testid="messages-private-toggle">
                    <input
                      type="checkbox"
                      checked={privateMode}
                      onChange={(e) => setPrivateMode(e.target.checked)}
                      className="accent-amber-400"
                    />
                    <span className={privateMode ? 'text-amber-300' : ''}>🔒 Private (E2E — hidden from admin) — Premium</span>
                  </label>
                )}
                <div className="flex gap-2">
                  <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                    placeholder={privateMode ? 'Private message…' : 'Type a message…'}
                    disabled={busy}
                    className={`bg-transparent border-[#1E1E24] text-sm ${privateMode ? 'border-amber-500/40' : ''}`}
                    data-testid="messages-input"
                  />
                  <Button onClick={send} disabled={busy || !text.trim()} className={`text-black hover:bg-[#06B6D4]/90 ${privateMode ? 'bg-amber-400 hover:bg-amber-300' : 'bg-[#06B6D4]'}`} data-testid="messages-send">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
